import { IBanList, IGameRecord, IMatchGamePlayer } from "../types/types";
import { hasCs15Data } from "./matchGameSides";
import {
  championIconById,
  championIconForBan,
  championIconForPlayer,
  DragonType,
  isFilledBanChampionId,
} from "./lolAssets";

export const PLAYER_ROLES = ["TOP", "JUNGLE", "MID", "BOTTOM", "SUPPORT"] as const;
export type PlayerRole = (typeof PLAYER_ROLES)[number];

export interface GameSupplementPlayerCs15 {
  role: PlayerRole;
  csAt15: string;
  championId?: number;
  championName?: string;
}

export interface TeamDragonLimits {
  maxDragons: number;
}

export interface ChampionDisplay {
  championId: number;
  championName: string;
  iconUrl: string;
  isValid: boolean;
  label: string;
}

const ELDER_DRAGON = "ELDER_DRAGON";

export const isElderDragon = (dragonType: string): boolean =>
  dragonType.trim().toUpperCase() === ELDER_DRAGON;

/** Total dragons taken by a team (elementals + elders, as reported by the API). */
export const getTeamDragonLimits = (
  game: Pick<IGameRecord, "blue_dragons" | "red_dragons">,
  side: "blue" | "red",
): TeamDragonLimits => ({
  maxDragons: side === "blue" ? game.blue_dragons : game.red_dragons,
});

export const resolveChampionFromId = (
  championId: number,
  championName?: string,
): ChampionDisplay | null => {
  if (!championId || Number.isNaN(championId)) return null;
  const name = championName?.trim() ?? "";
  return {
    championId,
    championName: name,
    iconUrl: championIconForBan(championId, name || undefined),
    isValid: championId > 0,
    label: name || `Champion ${String(championId)}`,
  };
};

export const resolveChampionFromInput = (
  value: string,
  championName?: string,
): ChampionDisplay | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const championId = Number(trimmed);
  if (Number.isNaN(championId) || championId <= 0) return null;
  return resolveChampionFromId(championId, championName);
};

export const resolveChampionFromPlayer = (player: IMatchGamePlayer): ChampionDisplay | null =>
  resolveChampionFromId(player.champion_id, player.champion_name);

export const resolveChampionFromBan = (ban: IBanList): ChampionDisplay | null =>
  resolveChampionFromId(ban.championId);

const normalizeSupplementRole = (role: string): PlayerRole | null => {
  const normalized = role.trim().toUpperCase();
  if (normalized === "TOP" || normalized === "JUNGLE") return normalized;
  if (normalized === "MID" || normalized === "MIDDLE") return "MID";
  if (normalized === "BOTTOM" || normalized === "ADC") return "BOTTOM";
  if (normalized === "SUPPORT" || normalized === "UTILITY") return "SUPPORT";
  return null;
};

const padBanSlots = (bans: IBanList[]): string[] => {
  const slots = Array.from({ length: 5 }, () => "");
  bans
    .slice()
    .sort((a, b) => a.pickTurn - b.pickTurn)
    .forEach((ban, index) => {
      if (index < slots.length && isFilledBanChampionId(ban.championId)) {
        slots[index] = String(ban.championId);
      }
    });
  return slots;
};

export const mapPlayersCs15FromRecord = (
  players: IMatchGamePlayer[] | null | undefined,
  riotTeamId: number,
): GameSupplementPlayerCs15[] => {
  const slots: GameSupplementPlayerCs15[] = PLAYER_ROLES.map((role) => ({ role, csAt15: "" }));
  const teamPlayers = (players ?? []).filter((player) => player.team_id === riotTeamId);

  for (const player of teamPlayers) {
    const role = normalizeSupplementRole(player.role);
    if (!role) continue;
    const slotIndex = slots.findIndex((entry) => entry.role === role);
    if (slotIndex < 0) continue;
    slots[slotIndex] = {
      role,
      csAt15: player.cs_at_15 > 0 ? String(player.cs_at_15) : "",
      championId: player.champion_id > 0 ? player.champion_id : undefined,
      championName: player.champion_name.trim() || undefined,
    };
  }

  return slots;
};

export const mapBanSlotsFromRecord = (bans: IBanList[] | null | undefined): string[] =>
  padBanSlots(bans ?? []);

export const BANS_PER_TEAM = 5;

export interface GameSupplementGaps {
  dragons: boolean;
  bans: boolean;
  csAt15: boolean;
}

export interface MatchSupplementGaps {
  dragons: boolean;
  bans: boolean;
  csAt15: boolean;
}

const countValidBans = (bans: IBanList[]): number =>
  bans.filter((ban) => isFilledBanChampionId(ban.championId)).length;

const teamDragonTypesComplete = (dragonsTaken: number, dragonTypes: string[]): boolean =>
  dragonsTaken === 0 || dragonTypes.length >= dragonsTaken;

/** Detect incomplete dragon types, bans, or CS@15 for a single game record. */
export const getGameSupplementGaps = (game: IGameRecord): GameSupplementGaps => {
  const blueTypes = game.blue_dragon_types ?? [];
  const redTypes = game.red_dragon_types ?? [];
  const blueBans = game.blue_bans ?? [];
  const redBans = game.red_bans ?? [];
  return {
    dragons:
      !teamDragonTypesComplete(game.blue_dragons, blueTypes) ||
      !teamDragonTypesComplete(game.red_dragons, redTypes),
    bans: countValidBans(blueBans) < BANS_PER_TEAM || countValidBans(redBans) < BANS_PER_TEAM,
    csAt15: !hasCs15Data(game.players ?? []),
  };
};

/** Aggregate supplement gaps across all games in a match. */
export const getMatchSupplementGaps = (games: IGameRecord[]): MatchSupplementGaps => {
  if (games.length === 0) {
    return { dragons: false, bans: false, csAt15: false };
  }

  const gaps = games.map(getGameSupplementGaps);
  return {
    dragons: gaps.some((gap) => gap.dragons),
    bans: gaps.some((gap) => gap.bans),
    csAt15: gaps.some((gap) => gap.csAt15),
  };
};

export const matchHasSupplementGaps = (gaps: MatchSupplementGaps): boolean =>
  gaps.dragons || gaps.bans || gaps.csAt15;

export const canAddDragonType = (
  selected: string[],
  _type: DragonType,
  limits: TeamDragonLimits,
): boolean => selected.length < limits.maxDragons;

export const dragonLimitSummary = (selected: string[], limits: TeamDragonLimits): string =>
  `Dragoneita ${String(selected.length)}/${String(limits.maxDragons)}`;

export const playerCs15IconUrl = (player: GameSupplementPlayerCs15): string | null => {
  if (player.championId && player.championId > 0) {
    return championIconById(player.championId);
  }
  if (player.championName?.trim()) {
    return championIconForPlayer({
      champion_id: 0,
      champion_name: player.championName,
    } as IMatchGamePlayer);
  }
  return null;
};
